import Box from "@material-ui/core/Box";
import Joi from "joi";
import React, {Fragment, useState} from "react";
import {logger} from "../services/LogService";
import OrganisationFunctionSelectionComponent from "../components/data/organisation/OrganisationFunctionSelectionComponent";
import ConsentDeclarationComponent from "../components/data/user/ConsentDeclarationComponent";
import PersonalDataComponent from "../components/data/user/PersonalDataComponent";
import EAButton from "../components/EAButton";
import EAButtonBar from "../components/EAButtonBar";
import EAFieldset from "../components/EAFieldset";
import RegistrationSuccessComponent from "../components/registration/RegistrationSuccessComponent";
import {LESER_ORGANISATION} from "../consts/funktionaleRollen";
import {funktionaleRollenOrganisation} from "../consts/valueLists";
import * as userService from "../services/AnwenderService";
import {getAnredeEndpoint, getTitelEndpoint} from "../services/WertelistenService";
import {findOrNull} from "../utils/ArrayUtils";
import {
    errorMessage,
    nullableNumber,
    regex,
    requiredNumber,
    requiredString,
    validate,
    VALIDATION_MESSAGE_TELEFON,
    validEmail,
} from "../utils/Validator";
import {getOrganisationenRegisterEndpoint} from "../services/OrganisationsService";
import {useHttpSortById} from "../utils/usePreformattedHttp";
import {REGEX_TELEFONNUMMER} from "../consts/regex";
import {toast} from "react-toastify";

const schema = {
    organisation: requiredNumber("Organisationsbezeichnung"),
    funktionaleRolle: Joi.array().items(Joi.string()),
    anrede: requiredNumber("Anrede"),
    titel: nullableNumber(),
    name: requiredString("Name"),
    vorname: requiredString("Vorname"),
    email: validEmail("E-Mail"),
    telefon: regex(REGEX_TELEFONNUMMER, true, VALIDATION_MESSAGE_TELEFON),
    mobil: regex(REGEX_TELEFONNUMMER, false, VALIDATION_MESSAGE_TELEFON),
    dsgvo: errorMessage(Joi.bool().valid(true), " "),
};

/**
 * View für die Registrierung eines Benutzers
 * @returns {JSX.Element}
 * @constructor
 */
function RegistrationView() {
    const [errors, setErrors] = useState({});
    const [registerIdle, setRegisterIdle] = useState(false);

    const [orgaOptions] = useHttpSortById(getOrganisationenRegisterEndpoint);
    const [titelOptions] = useHttpSortById(getTitelEndpoint);
    const [anredeOptions] = useHttpSortById(getAnredeEndpoint);

    const [registrationCompleted, setRegistrationCompleted] = useState(false);

    /* STATE */
    const [organisationsData, setOrganisation] = useState({
        organisation: null,
        funktionaleRolle: LESER_ORGANISATION,
    });
    const [person, setPerson] = useState({
        anrede: null,
        titel: null,
        name: "",
        vorname: "",
        email: "",
        telefon: "",
        mobil: "",
    });
    const [dsgvo, setDsgvo] = useState(false);

    /* END STATE */

    function buildRegisterItem() {
        const cleanPerson = {...person};

        cleanPerson.anrede = cleanPerson.anrede ? cleanPerson.anrede.id : null;
        cleanPerson.titel = cleanPerson.titel ? cleanPerson.titel.id : null;

        const cleanOrganisation = {...organisationsData};

        cleanOrganisation.funktionaleRolle = cleanOrganisation.funktionaleRolle ? [cleanOrganisation.funktionaleRolle] : [];

        return {
            ...cleanOrganisation,
            ...cleanPerson,
            dsgvo: dsgvo,
        };
    }

    function handleOrganisationChanged(field, value) {
        setOrganisation({...organisationsData, [field]: value});
    }

    function handlePersonChanged(field, value) {
        setPerson({...person, [field]: value});
    }

    function handleDsgvoChanged(value) {
        setDsgvo(value);
    }

    function handleRegister(event) {
        event.preventDefault();
        setRegisterIdle(true);
        const registrationData = buildRegisterItem();
        const newErrors = validate(registrationData, schema);
        setErrors(newErrors || {});

        if (newErrors) {
            setRegisterIdle(false);
            return;
        }

        userService
            .register(registrationData)
            .then(() => {
                setRegistrationCompleted(true);
            })
            .catch((error) => {
                if (error.response) {

                    Object.entries(error.response.data.validationErrors || {}).forEach(([key, value]) => {
                        toast.error(value);
                    });

                    setErrors(error.response.data.validationErrors || {});
                    setRegisterIdle(false);
                }
                logger.error(error);
            });
    }

    const orgaParam = {
        organisation: findOrNull(orgaOptions.data, (o) => o.id === organisationsData.organisation),
        funktionaleRolle: findOrNull(funktionaleRollenOrganisation, (o) => o.key === organisationsData.funktionaleRolle),
    };

    function buildRegistrationForm() {
        return (
            <Fragment>
                <div
                    className="viewScrollContainer"
                    style={{padding: "var(--vertical-spacing) var(--horizontal-spacing) 0 var(--horizontal-spacing)"}}
                >
                    <Box mt={2}>
                        <EAFieldset caption="Organisation">
                            <OrganisationFunctionSelectionComponent
                                organisations={orgaOptions}
                                functions={funktionaleRollenOrganisation}
                                organisation={orgaParam}
                                errors={errors}
                                handleOrganisationChanged={handleOrganisationChanged}
                            />
                        </EAFieldset>
                    </Box>
                    <Box mt={4}>
                        <EAFieldset caption="Persönliche Angaben">
                            <PersonalDataComponent
                                person={person}
                                anredeOptions={anredeOptions}
                                titelOptions={titelOptions}
                                handlePersonChanged={handlePersonChanged}
                                errors={errors}
                            />
                        </EAFieldset>
                    </Box>
                    <Box mt={4}>
                        <EAFieldset caption="Einwilligungserklärung">
                            <ConsentDeclarationComponent value={dsgvo} handleDsgvoChanged={handleDsgvoChanged} errors={errors}/>
                        </EAFieldset>
                    </Box>
                </div>
                <EAButtonBar style={{margin: "var(--vertical-spacing) var(--horizontal-spacing)"}}>
                    <p style={{marginRight: "auto"}}>
                        <i>(*) Pflichtfelder</i>
                    </p>
                    <EAButton
                        id="Registrierung.Submit"
                        label="Registrieren"
                        color="primary"
                        style={{float: "right"}}
                        loading={registerIdle}
                        disabled={registerIdle}
                        onClick={handleRegister}
                    />
                </EAButtonBar>
            </Fragment>
        );
    }

    return (
        <div className="viewScrollContainerParent">
            {registrationCompleted ? <RegistrationSuccessComponent email={person.email}/> : buildRegistrationForm()}
        </div>
    );
}

export default RegistrationView;
